"use client";

import { useCallback } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { AutocompleteResult, VariantFilterValue } from "@/lib/api/types";
import { hasEntityForm } from "@/lib/search/entity-variants";
import type { PresetTypeSlug } from "@/features/presets";
import {
  BulkModifyDropdown,
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
  FilterLabel,
  PresetButton,
} from "../shared";
import { FilterDurationInput } from "@/features/search/components/filters/input/duration-input";
import { FilterJobTitleInput } from "@/features/search/components/filters/input/job-title-input";

const JOB_TITLE_LEVELS: { value: string; label: string; icon: string }[] = [
  { value: "owner", label: "Owner", icon: "IconCrown" },
  { value: "partner", label: "Partner", icon: "IconHandshake" },
  { value: "cxo", label: "C-Suite", icon: "IconStar" },
  { value: "vp", label: "Vice President", icon: "IconTrophy" },
  { value: "director", label: "Director", icon: "IconCompassRound" },
  { value: "manager", label: "Manager", icon: "IconPeople2" },
  { value: "senior", label: "Senior", icon: "IconMedal" },
  { value: "entry", label: "Entry", icon: "IconDoor" },
  { value: "training", label: "Trainee", icon: "IconGraduateCap" },
  { value: "unpaid", label: "Trainee (group)", icon: "IconSchool" },
];

const JOB_TITLE_LEVEL_LABELS: Record<string, string> = Object.fromEntries(JOB_TITLE_LEVELS.map((l) => [l.value, l.label]));

const JOB_TITLE_ROLES: { value: string; label: string; icon: string }[] = [
  { value: "customer_service", label: "Customer Service", icon: "IconSupport" },
  { value: "design", label: "Design", icon: "IconPaintBrush" },
  { value: "education", label: "Education", icon: "IconBooks" },
  { value: "engineering", label: "Engineering", icon: "IconCode" },
  { value: "finance", label: "Finance", icon: "IconBank" },
  { value: "health", label: "Health", icon: "IconHeart" },
  { value: "human_resources", label: "Human Resources", icon: "IconPeople2" },
  { value: "legal", label: "Legal", icon: "IconJudgeGavel" },
  { value: "marketing", label: "Marketing", icon: "IconMegaphone" },
  { value: "media", label: "Media", icon: "IconCamera1" },
  { value: "operations", label: "Operations", icon: "IconSettingsGear1" },
  { value: "public_relations", label: "Public Relations", icon: "IconMicrophone" },
  { value: "real_estate", label: "Real Estate", icon: "IconBuildings" },
  { value: "sales", label: "Sales", icon: "IconTrending4" },
  { value: "trades", label: "Trades", icon: "IconToolbox" },
];

const JOB_TITLE_ROLE_LABELS: Record<string, string> = Object.fromEntries(JOB_TITLE_ROLES.map((r) => [r.value, r.label]));

export function FilterJobSection({ onOpenPreset }: { onOpenPreset?: (type: PresetTypeSlug) => void }) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const titles = filters.job_titles ?? [];
  const levels = filters.job_title_levels ?? [];
  const roles = filters.job_title_roles ?? [];

  const updateTitle = useCallback(
    (index: number, updated: VariantFilterValue) => {
      const next = [...titles];
      next[index] = updated;
      updateFilter("job_titles", next);
    },
    [titles, updateFilter],
  );

  const addTitle = (title: VariantFilterValue) => {
    if (hasEntityForm(titles, title.text ?? "")) return;
    updateFilter("job_titles", [...titles, title]);
  };

  const removeTitle = useCallback(
    (index: number) => {
      updateFilter("job_titles", titles.filter((_, j) => j !== index));
    },
    [titles, updateFilter],
  );

  const bulkSetScope = useCallback(
    (scope: "c" | "p" | "cp") => {
      updateFilter("job_titles", titles.map((jt) => ({ ...jt, scope })));
    },
    [titles, updateFilter],
  );

  const bulkExclude = useCallback(() => {
    updateFilter("job_titles", titles.map((jt) => ({ ...jt, mode: "exclude" as const })));
  }, [titles, updateFilter]);

  const bulkUnexclude = useCallback(() => {
    updateFilter("job_titles", titles.map((jt) => ({ ...jt, mode: "include" as const })));
  }, [titles, updateFilter]);

  const bulkRequire = useCallback(() => {
    updateFilter("job_titles", titles.map((jt) => ({ ...jt, required: true })));
  }, [titles, updateFilter]);

  const bulkUnrequire = useCallback(() => {
    updateFilter("job_titles", titles.map((jt) => ({ ...jt, required: false })));
  }, [titles, updateFilter]);

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full">
          <FilterLabel>Job Titles</FilterLabel>
          {titles.length > 0 && (
            <BulkModifyDropdown
              onSetScope={bulkSetScope}
              onExcludeAll={bulkExclude}
              onUnexcludeAll={bulkUnexclude}
              onRequireAll={bulkRequire}
              onUnrequireAll={bulkUnrequire}
            />
          )}
        </div>
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="job_titles"
            placeholder="Search..."
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              addTitle({
                canonical_id: result.canonical_id ?? undefined,
                text: result.value,
                scope: "cp",
                required: false,
              });
            }}
            onFreeText={(text: string) => {
              addTitle({ text, scope: "cp", required: false });
            }}
          />
          <PresetButton label="Job Title Presets" onClick={() => onOpenPreset?.("job-titles")} />
        </div>
      </div>

      {titles.map((jt, i) => (
        <FilterJobTitleInput
          key={`${jt.text}-${i}`}
          title={jt}
          fallbackScope={filters.jt_filter_type}
          variantsField="job_titles"
          onChange={(updated) => updateTitle(i, updated)}
          onRemove={() => removeTitle(i)}
        />
      ))}

      {titles.length > 1 && (
        <div className="flex justify-end">
          <ClearChipsButton onClick={() => updateFilter("job_titles", undefined)} />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Job Title Levels</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="job_title_levels"
            placeholder="Search..."
            staticItems={JOB_TITLE_LEVELS}
            selectedValues={levels.map((l) => l.text ?? "")}
            allowFreeText={false}
            onSelect={(result: AutocompleteResult) => {
              const idx = levels.findIndex((l) => l.text === result.value);
              if (idx >= 0) {
                const next = levels.filter((_, i) => i !== idx);
                updateFilter("job_title_levels", next.length > 0 ? next : undefined);
              } else {
                updateFilter("job_title_levels", [...levels, { text: result.value, required: false }]);
              }
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {levels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {levels.map((lv, i) => {
            const excluded = lv.mode === "exclude";
            const required = lv.required ?? false;
            return (
              <FilterChip
                key={i}
                label={JOB_TITLE_LEVEL_LABELS[lv.text ?? ""] ?? lv.text ?? ""}
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...levels];
                  next[i] = { ...lv, mode: excluded ? "include" : "exclude" };
                  updateFilter("job_title_levels", next);
                }}
                onToggleRequired={() => {
                  const next = [...levels];
                  next[i] = { ...lv, required: !required };
                  updateFilter("job_title_levels", next);
                }}
                onRemove={() => updateFilter("job_title_levels", levels.filter((_, j) => j !== i))}
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter("job_title_levels", undefined)} />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Job Title Roles</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="job_title_roles"
            placeholder="Search..."
            staticItems={JOB_TITLE_ROLES}
            selectedValues={roles.map((r) => r.text ?? "")}
            allowFreeText={false}
            onSelect={(result: AutocompleteResult) => {
              const idx = roles.findIndex((r) => r.text === result.value);
              if (idx >= 0) {
                const next = roles.filter((_, i) => i !== idx);
                updateFilter("job_title_roles", next.length > 0 ? next : undefined);
              } else {
                updateFilter("job_title_roles", [...roles, { text: result.value, required: false }]);
              }
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {roles.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {roles.map((rl, i) => {
            const excluded = rl.mode === "exclude";
            const required = rl.required ?? false;
            return (
              <FilterChip
                key={i}
                label={JOB_TITLE_ROLE_LABELS[rl.text ?? ""] ?? rl.text ?? ""}
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...roles];
                  next[i] = { ...rl, mode: excluded ? "include" : "exclude" };
                  updateFilter("job_title_roles", next);
                }}
                onToggleRequired={() => {
                  const next = [...roles];
                  next[i] = { ...rl, required: !required };
                  updateFilter("job_title_roles", next);
                }}
                onRemove={() => updateFilter("job_title_roles", roles.filter((_, j) => j !== i))}
              />
            );
          })}
          <ClearChipsButton onClick={() => updateFilter("job_title_roles", undefined)} />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Time Spent at Current Role</FilterFieldLabel>
        <FilterFieldContent>
          <div className="flex w-full items-center">
            <span className="mr-1.5 whitespace-nowrap text-sm font-normal text-contrast/80">Between</span>
            <FilterDurationInput
              value={filters.time_current_role?.min_months ?? undefined}
              onChange={(v) => {
                const existing = filters.time_current_role ?? {};
                const next = { ...existing, min_months: v };
                updateFilter("time_current_role", next.min_months == null && next.max_months == null ? undefined : next);
              }}
              placeholder="No Min"
            />
            <span className="mx-1.5 whitespace-nowrap text-sm font-normal text-contrast/80">and</span>
            <FilterDurationInput
              value={filters.time_current_role?.max_months ?? undefined}
              onChange={(v) => {
                const existing = filters.time_current_role ?? {};
                const next = { ...existing, max_months: v };
                updateFilter("time_current_role", next.min_months == null && next.max_months == null ? undefined : next);
              }}
              placeholder="No Max"
            />
          </div>
        </FilterFieldContent>
      </FilterFieldContainer>

      <FilterFieldContainer>
        <FilterFieldLabel>Average Tenure</FilterFieldLabel>
        <FilterFieldContent>
          <div className="flex w-full items-center">
            <span className="mr-1.5 whitespace-nowrap text-sm font-normal text-contrast/80">Between</span>
            <FilterDurationInput
              value={filters.average_tenure_min_months ?? undefined}
              onChange={(v) => updateFilter("average_tenure_min_months", v)}
              placeholder="No Min"
            />
            <span className="mx-1.5 whitespace-nowrap text-sm font-normal text-contrast/80">and</span>
            <FilterDurationInput value={undefined} onChange={() => {}} placeholder="No Max" />
          </div>
        </FilterFieldContent>
      </FilterFieldContainer>
    </>
  );
}
